/**
 * Canonical paywall analytics vocabulary.
 *
 * Every paywall screen emits the SAME small set of event names and distinguishes itself
 * with a `paywall: PaywallSlug` property, rather than inventing a per-screen event name.
 * This keeps Amplitude's event-name cardinality flat and lets a single funnel be built
 * across every paywall (View → Trial Selection → Purchase, plus dismiss/dwell).
 *
 * Use {@link usePaywallAnalytics} for the View / Trial Selection / Purchase funnel and
 * {@link usePaywallDismissTracking} for Dismissed / Refocused. The remaining constants are
 * fired by the shared payment infrastructure (PaymentIntentScreen, setupStripe*, etc.).
 *
 * Legacy per-screen names (e.g. "Email Confirmation Checkout Viewed") are still emitted
 * alongside the standard names during the dashboard-migration window via the `legacy` map
 * passed to the hook — see that hook's docs. Delete a screen's `legacy` block once its
 * Amplitude charts have been rebuilt on the standard names.
 */
export const PAYWALL_EVENTS = {
  /** Screen mounted / became the active paywall. */
  viewed: '[Web Payments] Paywall Viewed',
  /** User picked a trial/plan option (before purchasing). */
  trialSelection: '[Web Payments] Paywall Trial Selection',
  /** Purchase confirmed by the backend. */
  purchaseSuccess: '[Web Payments] Paywall Purchase Success',
  /** Page first hidden without a purchase (dwell/leave). Fired by usePaywallDismissTracking. */
  dismissed: '[Web Payments] Paywall Dismissed',
  /** Page became visible again after a dismiss. Fired by usePaywallDismissTracking. */
  refocused: '[Web Payments] Paywall Refocused',
  /**
   * The user scrolled the paywall. Fired by usePaywallScrollDepth, at most once per
   * `depthBucket` (25/50/75/100) per page view, and once per named `section` when that
   * section is actually on screen.
   *
   * Exists because a paywall that puts content below the CTA can only be judged against
   * how many people ever reach it. The killed /social-proof screen had a fire-once
   * `Social Proof Page Scrolled` that answered "did they scroll at all" and nothing more;
   * this replaces it with a bucketed, cross-paywall-comparable version.
   */
  scrolled: '[Web Payments] Paywall Scrolled',
  /** Apple/Google Pay sheet closed. Fired by PaymentIntentScreen. */
  paymentSheetDismissed: '[Web Payments] Payment Sheet Dismissed',
  /** A payment method surfaced to the user. Carries `paymentType: PaymentType`. */
  paymentMethodShown: '[Web Payments] Paywall Payment Method Shown',
  /** Stripe setup for the paywall failed before any payment UI could render. */
  stripeSetupFailed: '[Web Payments] Paywall Stripe Setup Failed',
  /**
   * Stripe.js itself failed to load (blocked or unreachable js.stripe.com). Fired once per page by the Stripe loader
   * after its automatic retry, with `retried: true`, `recovered` (did the retry load it), `linkOut` (the app opened
   * this visit), and `path`. When `recovered` is false the page shows `StripeLoadFallback`.
   */
  stripeLoadFailed: '[Web Payments] Paywall Stripe Load Failed',
  /**
   * The person acted on `StripeLoadFallback`. `action`: `try_again` (with `recovered` and `attempt`, the number of
   * fresh loads tried including the automatic one) or `return_to_app` (link-out visits only). Carries `linkOut`.
   */
  stripeLoadFallbackAction: '[Web Payments] Paywall Stripe Load Fallback Action',
  /**
   * A meaningful submit was attempted (gift claim, recipient assignment, …).
   * Paired with `actionFailed` so failure rate is attempts minus successes — without the
   * attempt event, failures can't even be computed by subtraction.
   */
  actionAttempted: '[Web Payments] Paywall Action Attempted',
  /**
   * The attempt failed. Carries `action` and a bounded `reason`
   * (`not_found` | `transport` | `server`) — never the raw error message, which is
   * unbounded and can carry user data.
   *
   * Every user-visible error must fire this. The gifting redemption bug fixed in
   * Hypnos#3630 broke claiming for a whole class of recipients and was invisible to every
   * dashboard, because the failure path reported to the user and told analytics nothing.
   */
  actionFailed: '[Web Payments] Paywall Action Failed',

  // I21 second-chance dismiss offer. Fired by PaymentIntentScreen when the offer is
  // surfaced via the ✕/idle affordance, and as the user accepts / declines / completes it.
  // All carry `paywall`, `code`, `offerCode`, and a `source` tag (corner_x_immediate |
  // corner_x_delayed | idle_timeout).
  /**
   * The dismiss-treatment arm for this page view, fired once when the payment UI is ready
   * (the arm is final then: GrowthBook only knows the user's personId after Stripe setup).
   * Carries `dismissTreatment` (off | immediate-x | delayed-x | idle-time), so Amplitude can
   * split by arm without reading it off Paywall Viewed, which fires before it's known.
   */
  dismissTreatmentAssigned: '[Web Payments] Dismiss Treatment Assigned',
  /** Second-chance offer sheet shown. */
  dismissOfferShown: '[Web Payments] Dismiss Offer Shown',
  /** User tapped the offer's accept CTA. */
  dismissOfferAccepted: '[Web Payments] Dismiss Offer Accepted',
  /** User dismissed the offer without accepting. */
  dismissOfferDeclined: '[Web Payments] Dismiss Offer Declined',
  /** Second-chance offer purchase confirmed. */
  dismissOfferPurchaseSuccess: '[Web Payments] Dismiss Offer Purchase Success',
} as const;

/** Bounded failure classification for `actionFailed`. Never send the raw message. */
export type FailureReason = 'not_found' | 'transport' | 'server' | 'missing_token' | 'send_limit';

export type PaymentType = 'apple' | 'google' | 'card';

/**
 * Registry of every paywall `paywall` slug. Adding a paywall means adding its slug here.
 * Keeping the list central makes the full set greppable and prevents typo drift
 * (e.g. `email-confirmation` vs `email_confirmation_checkout`) creating phantom paywalls
 * in Amplitude. Slugs are kebab-case; student variants reuse the base slug and carry an
 * `isStudent` property rather than a separate slug.
 */
export const PAYWALL_SLUGS = [
  // emailConfirmation-0424 (highest traffic)
  'email-confirmation-checkout',
  'email-confirmation-design-trial',
  'email-confirmation-bottom-sheet',
  'email-confirmation-feature-card',
  'email-confirmation-trial',
  'choose-your-price',
  'gender',
  // trialCancelPaywall
  'special-offer',
  'trial-cancel',
  // winback offers (the app's link-out carousel)
  'two-week-offer',
  'student-two-week-offer',
  'forty-percent-off',
  'student-forty-percent-off',
  // expiredPaywall
  'expired',
  // D11/B18 — one-tap resume for expired web subscribers (/resume). Funnel: Viewed (with
  // `eligible`, `reason`) → Trial Selection → Action Attempted `resume` → Purchase Success, or
  // Action Failed `resume` with a `fallbackReason` when it hands off to /expired.
  'resume',
  // nativeWebPaywall
  'native-web',
  // aiRecap
  'ai-recap',
  // claude-generated experiments
  'monthly',
  'email-campaign',
  'social-proof',
  'energy-focus',
  'per-day-rate',
  'recommend-099',
  'renewal-transparency',
  'outcome-timeline-bedtime',
  'outcome-timeline-sleep',
  'outcome-timeline-sleep-v2',
  'data-viz-sleep',
  'data-viz-bedtime',
  'name-greeting',
  'name-emphasized',
  'name-hero',
  'name-personal-note',
  'ai-bundle-trial-choice',
  'ai-bundle-checkout',
  'ai-bundle-paid-trial',
  // I63 — plan-card label framing (isolates B10's experimental_control relabel)
  'trial-framed-plans',
  // I36 — social proof BELOW the plan grid. Both arms are /name-greeting with a credibility
  // block appended under the fold; nothing above the CTA changes. Distinct from the killed
  // 'social-proof' slug, which was a full interstitial screen in FRONT of the plan grid.
  'press-badges',
  'social-proof-scroll',
  // graduation-signaling A/B (B10) — one slug per arm
  'graduation-signaling',
  'graduation-control',
  // Gift Rise (D07). Not a paywall experiment, but it reuses the same funnel vocabulary so
  // the purchase lands in the standard View → Purchase charts instead of needing its own.
  'gift-purchase',
  'gift-success',
  'gift-redeem',
] as const;

export type PaywallSlug = typeof PAYWALL_SLUGS[number];
